export const MARKETPLACE_CODES = ["DE", "IT", "FR", "BE", "NL", "IE"] as const;
export type MarketplaceCode = (typeof MARKETPLACE_CODES)[number];

export const MARKETPLACE_META: Record<
  MarketplaceCode,
  { label: string; currency: "EUR"; vat: number; marketplaceId: string }
> = {
  DE: { label: "Deutschland", currency: "EUR", vat: 19, marketplaceId: "A1PA6795UKMFR9" },
  IT: { label: "Italien", currency: "EUR", vat: 22, marketplaceId: "APJ6JRA9NG5V4" },
  FR: { label: "Frankreich", currency: "EUR", vat: 20, marketplaceId: "A13V1IB3VIYZZH" },
  BE: { label: "Belgien", currency: "EUR", vat: 21, marketplaceId: "AMEN7PMS3EDWL" },
  NL: { label: "Niederlande", currency: "EUR", vat: 21, marketplaceId: "A1805IZSGTT6HS" },
  IE: { label: "Irland", currency: "EUR", vat: 23, marketplaceId: "A28R8C7NBKEWEA" },
};

export interface User {
  id?: string;
  email: string;
  name?: string;
  role: "admin" | string;
}

export interface Marketplace {
  code: MarketplaceCode;
  marketplaceId: string;
  currency: string;
  defaultVatRate: number;
  enabled: boolean;
  connected: boolean;
  complianceReady?: boolean;
  complianceNotes?: string[];
}

export type ConnectionState = "connected" | "degraded" | "disconnected" | "unknown";

export interface AmazonConnection {
  status: ConnectionState;
  sellerIdMasked?: string;
  region?: string;
  lastTestAt?: string;
  lastSuccessfulSyncAt?: string;
  message?: string;
}

export interface DashboardData {
  connection?: AmazonConnection;
  listingCount?: number;
  healthyCount?: number;
  missingForeignCount?: number;
  issueCount?: number;
  activeJobs?: number;
  failedJobs?: number;
  lastSyncAt?: string;
  nextSyncAt?: string;
  finance?: FinanceDashboardSummary;
}

export type WorkView = "now" | "waiting" | "opportunities";

export interface WorkTask {
  id: string;
  kind: "issue" | "rollout";
  state: WorkView;
  sellerSku: string;
  marketplaceCode: MarketplaceCode;
  fulfillmentMode?: FulfillmentMode | null;
  productTitle: string;
  imageUrl?: string | null;
  asin?: string | null;
  title: string;
  message: string;
  nextStep: string;
  actionLabel?: string;
  fieldGuidance?: IssueFieldGuidance | null;
  label: string;
  priority: number;
  issueId?: string;
  issueIds: string[];
  issueCount: number;
  evidenceKind?: QualityEvidenceKind;
  nextCheckAt?: string | null;
  retrying?: boolean;
  lastConfirmedAt?: string | null;
  missingCountries?: MarketplaceCode[];
}

export interface WorkQueueResponse {
  items: WorkTask[];
  total: number;
  summary: Record<WorkView, number>;
  nextOffset: number | null;
  backgroundJobs: Array<Pick<Job, "id" | "type" | "status" | "sellerSku" | "message" | "createdAt">>;
  backgroundJobCount: number;
  listingCount: number;
  lastScanAt?: string | null;
  lastScanStatus?: string | null;
  generatedAt: string;
}

export interface CatalogItem {
  asin: string;
  title: string;
  brand?: string;
  productType?: string;
  imageUrl?: string;
  identifiers?: Array<{ type: string; value: string }>;
  marketplace?: MarketplaceCode;
  variationCount?: number;
  existingOffers?: Array<{
    sellerSku: string;
    fulfillmentMode: FulfillmentMode;
    marketplace: MarketplaceCode;
  }>;
}

export interface CatalogSearchResponse {
  items: CatalogItem[];
  total?: number;
  nextToken?: string | null;
}

export interface Restriction {
  marketplace?: MarketplaceCode;
  conditionType?: string;
  reasonCode?: string;
  message: string;
  links?: Array<{ title?: string; url?: string }>;
}

export interface ProductType {
  name: string;
  displayName?: string;
  marketplaceIds?: string[];
}

export type JsonSchemaPrimitiveType = "string" | "number" | "integer" | "boolean";

export interface JsonSchemaNode {
  $ref?: string;
  type?: JsonSchemaPrimitiveType | "array" | "object" | Array<string>;
  title?: string;
  description?: string;
  properties?: Record<string, JsonSchemaNode>;
  required?: string[];
  items?: JsonSchemaNode;
  enum?: unknown[];
  enumNames?: string[];
  default?: unknown;
  const?: unknown;
  examples?: unknown[];
  minLength?: number;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
  minItems?: number;
  maxItems?: number;
  format?: string;
  hidden?: boolean;
  editable?: boolean;
  allOf?: JsonSchemaNode[];
  anyOf?: JsonSchemaNode[];
  oneOf?: JsonSchemaNode[];
  definitions?: Record<string, JsonSchemaNode>;
  $defs?: Record<string, JsonSchemaNode>;
  [key: string]: unknown;
}

export interface ProductTypeSchemaLink {
  link?:
    | string
    | {
        resource?: string;
        verb?: string;
      };
  checksum?: string;
  [key: string]: unknown;
}

export interface ProductTypePropertyGroup {
  title?: string;
  description?: string;
  propertyNames?: string[];
}

export interface ProductTypeDefinitionResponse {
  productType?: string;
  displayName?: string;
  requirements?: string;
  requirementsEnforced?: string;
  locale?: string;
  propertyGroups?: Record<string, ProductTypePropertyGroup>;
  schema?: JsonSchemaNode | ProductTypeSchemaLink;
  resolvedSchema?: JsonSchemaNode;
  schemaDefinition?: JsonSchemaNode;
  definition?: {
    schema?: JsonSchemaNode | ProductTypeSchemaLink;
    resolvedSchema?: JsonSchemaNode;
    propertyGroups?: Record<string, ProductTypePropertyGroup>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export type FulfillmentMode = "FBM" | "FBA";
export type ListingMode = "MATCH_EXISTING_ASIN" | "CREATE_NEW_ASIN";

export interface MediaLocator {
  assetId: string;
  role: string;
  order: number;
}

export interface ListingDraft {
  mode: ListingMode;
  sellerSku: string;
  gtin?: string;
  billbeeId?: string;
  identityId?: string;
  skuUnit?: { kind: "single" | "pack" | "bundle"; quantity: number; variant?: string };
  asin?: string;
  productType?: string;
  condition: string;
  targetMarketplaces: MarketplaceCode[];
  fulfillmentMode: FulfillmentMode;
  startPrices: Partial<Record<MarketplaceCode, number>>;
  shippingTemplates: Partial<Record<MarketplaceCode, string>>;
  attributes: Record<string, unknown>;
  mediaLocators: MediaLocator[];
  sourceDePrice?: number;
}

export interface PreviewDiff {
  path?: string;
  field?: string;
  before?: unknown;
  after?: unknown;
  operation?: "add" | "replace" | "remove" | string;
}

export interface MarketplacePreview {
  code: MarketplaceCode;
  exists: boolean;
  operation: string;
  diff: PreviewDiff[];
  repricing?: MarketplaceRepricingState;
  warnings: string[];
  errors: string[];
}

export interface ListingPreview {
  previewId: string;
  expiresAt: string;
  draft: ListingDraft;
  marketplaces: MarketplacePreview[];
  canSubmit: boolean;
  confirmationToken: string;
  warnings?: string[];
  errors?: string[];
}

export type ListingStatus =
  | "active"
  | "missing"
  | "inactive"
  | "suppressed"
  | "restricted"
  | "pending"
  | "unknown";

export interface MarketplaceListingIssue {
  code?: string;
  severity?: string;
  message: string;
  categories?: string[];
  attributeNames?: string[];
  enforcementActions?: string[];
  rawEnforcementActions?: string[];
  reportedEnforcementActions?: string[];
  exemptionStatus?: string | null;
  exemptionExpiry?: string | null;
  exemptionActive?: boolean;
  source?: string;
  evidenceClass?: string;
  explanation?: string;
  recommendation?: string;
  fixKind?: string;
  category?: QualityCategory;
  reason?: string;
  statusChangedAt?: string;
}

export interface MarketplaceListingState {
  status: ListingStatus;
  price?: number;
  currency?: string;
  fulfillmentMode?: FulfillmentMode;
  shippingTemplateId?: string;
  shippingTemplate?: string;
  shippingConfigurationStatus?: ShippingConfigurationStatus;
  quantity?: number;
  imageWidth?: number;
  imageHeight?: number;
  issues?: MarketplaceListingIssue[];
  buyable?: boolean;
  discoverable?: boolean;
  issuesCheckedAt?: string;
  lastVerifiedAt?: string;
  live?: boolean;
  repricing?: MarketplaceRepricingState;
}

export type ShippingConfigurationStatus =
  | "configured"
  | "amazon_fulfilled"
  | "missing"
  | "unknown"
  | "not_applicable";

export interface ShippingOverviewItem {
  sellerSku: string;
  asin?: string;
  title: string;
  imageUrl?: string;
  marketplace: MarketplaceCode;
  status: ListingStatus;
  fulfillmentMode?: FulfillmentMode;
  shippingTemplateId?: string;
  shippingTemplate?: string;
  shippingConfigurationStatus: ShippingConfigurationStatus;
  lastVerifiedAt?: string;
  updatedAt?: string;
}

export interface ShippingOverviewCountrySummary {
  total: number;
  fbm: number;
  fba: number;
  configuredFbm: number;
  missingFbmTemplate: number;
  unknown: number;
  templates: Array<{ id: string; name: string; listings: number }>;
}

export interface ShippingOverviewResponse {
  items: ShippingOverviewItem[];
  total: number;
  marketplace: MarketplaceCode;
  summary: Partial<Record<MarketplaceCode, ShippingOverviewCountrySummary>>;
  refreshedAt?: string;
}

export interface ShippingSettings {
  defaultTemplates: Partial<Record<MarketplaceCode, string>>;
  amazonTemplateCreation: {
    supportedByApi: boolean;
    sellerCentralUrl: string;
    message: string;
  };
}

export type RepricingStatus = "active" | "inactive" | "unknown";

export interface MarketplaceRepricingState {
  status: RepricingStatus;
  active: boolean | null;
  linked?: boolean | null;
  priceChangeAllowed: boolean;
  source: "amazon-repricer" | string;
}

export interface ActiveRepricingImportItem {
  titel: string;
  ean: string;
  sku: string;
  laender_aktiv: string[];
  laender_nicht_aktiv: string[];
}

export interface ListingMatrixRow {
  sellerSku: string;
  asin?: string;
  title: string;
  imageUrl?: string;
  fulfillmentMode?: FulfillmentMode;
  marketplaces: Partial<Record<MarketplaceCode, MarketplaceListingState>>;
  updatedAt?: string;
}

export interface ListingMatrixResponse {
  items: ListingMatrixRow[];
  total: number;
  nextCursor?: string | null;
  repricing?: {
    status: "ok" | "unavailable";
    source: string;
    priceChangesFailClosed: boolean;
    message?: string | null;
  };
}

export interface ListingDetailResponse {
  sellerSku?: string;
  asin?: string;
  title?: string;
  imageUrl?: string;
  productType?: string;
  product_type?: string;
  fulfillmentMode?: FulfillmentMode;
  fulfillment_mode?: FulfillmentMode | string;
  marketplaces?: Partial<Record<MarketplaceCode, MarketplaceListingState>>;
  mediaAssets?: MediaAsset[];
  [key: string]: unknown;
}

export type MediaState =
  | "prepared"
  | "reserved"
  | "submitted"
  | "accepted"
  | "live"
  | "rejected"
  | "superseded";

export type QualityEvidenceKind =
  | "amazon_confirmed"
  | "amazon_live_state"
  | "local_measured"
  | "inferred";

export type QualitySeverity = "critical" | "high" | "medium" | "low";

export type QualityCategory =
  | "suppression"
  | "media"
  | "content"
  | "compliance"
  | "restriction"
  | "offer";

export type QualityFixActionKind =
  | "open_media_studio"
  | "refresh_listing"
  | "open_seller_central"
  | "change_price"
  | "none";

export interface QualityEvidence {
  kind: QualityEvidenceKind;
  source: string;
  label: string;
  observedAt?: string;
  confidence?: number;
}

export interface AffectedMedia {
  assetId?: string | null;
  role?: string | null;
  url?: string | null;
}

export type IssueFixability =
  | "API_ATTRIBUTE"
  | "API_PRICE"
  | "API_IMAGE"
  | "REFRESH"
  | "SELLER_CENTRAL"
  | "SUPPORT_CASE";

export type SellerCentralTarget = "inventory" | "performance" | "shipping" | "root";

export interface IssuePlaybook {
  key: string;
  title: string;
  cause: string;
  impact: string;
  steps: string[];
  fixability: IssueFixability | string;
  canFixViaApi: boolean;
  attributes: string[];
  sellerCentralPath: string;
  sellerCentralTarget: SellerCentralTarget | string;
  supportCaseText: string;
  confidence: "high" | "medium" | "low" | string;
  sources: string[];
  knowledgeSource: string;
  knowledgeLabel: string;
  matchedCode?: string | null;
}

export interface PlaybookCatalogEntry {
  key: string;
  codes: string[];
  title: string;
  cause: string;
  fixability: IssueFixability | string;
  confidence: string;
  sources: string[];
}

export interface PlaybookCatalogResponse {
  items: PlaybookCatalogEntry[];
  knowledgeSource: string;
  knowledgeLabel: string;
  disclaimer: string;
}

export interface ContentQualityIssue {
  id: string;
  sellerSku: string;
  asin?: string;
  title: string;
  imageUrl?: string;
  marketplaceCode: MarketplaceCode;
  listingStatus: ListingStatus;
  currentPrice?: number | null;
  currency?: string;
  repricing?: MarketplaceRepricingState;
  repricerUrl?: string;
  followup?: {
    state: "waiting" | "persistent" | "check_failed";
    confirmedAt: string;
    dueAt: string;
    nextCheckAt?: string | null;
    checkedAt?: string | null;
    message?: string;
  };
  severity: QualitySeverity;
  category: QualityCategory;
  code?: string;
  message: string;
  explanation: string;
  recommendation: string;
  evidence: QualityEvidence;
  affectedFields: string[];
  fieldGuidance?: IssueFieldGuidance | null;
  affectedMedia: AffectedMedia[];
  enforcementActions: string[];
  reportedEnforcementActions?: string[];
  exemptionStatus?: string | null;
  exemptionExpiry?: string | null;
  exemptionActive?: boolean;
  fixAction: {
    kind: QualityFixActionKind;
    label: string;
  };
  playbook?: IssuePlaybook;
  lastConfirmedAt?: string;
}

export interface IssueFieldGuidance {
  title: string;
  nextStep: string;
  message: string;
  fieldsKnown: boolean;
  fields: Array<{
    name: string;
    label: string;
    currentValue: unknown;
    currentValueKnown: boolean;
  }>;
}

export interface AttributeFixField {
  name: string;
  label: string;
  description: string;
  currentValue: unknown;
  currentValueKnown: boolean;
  inputValue: string | number | boolean | null;
  valueType: "string" | "integer" | "number" | "boolean" | null;
  options: Array<{ value: string | number | boolean; label: string }>;
  constraints: {
    minLength?: number;
    maxLength?: number;
    minimum?: number;
    maximum?: number;
    pattern?: string;
  };
  editable: boolean;
  reason?: string | null;
  template: unknown;
  valuePath: Array<string | number>;
}

export interface AttributeFixContext {
  sellerSku: string;
  marketplaceCode: MarketplaceCode;
  productType: string | null;
  checkedAt: string | null;
  fields: AttributeFixField[];
  warnings: string[];
}

export interface ContentQualitySummary {
  suppressed: number;
  amazonErrors: number;
  mediaProblems: number;
  recommendations: number;
  totalOpen: number;
  lastScanAt?: string;
  lastScanStatus?: string | null;
}

export type ContentQualityMethodology =
  | string
  | string[]
  | Record<string, unknown>
  | null;

export interface AccountHealthMetric {
  key: string;
  label: string;
  count: number;
  status: string;
  marketplaces: MarketplaceCode[];
  targetValue?: string | number | null;
  targetCondition?: string | null;
}

export interface AccountHealthStatus {
  marketplaceId?: string | null;
  marketplaceCode?: MarketplaceCode | null;
  status: string;
}

export interface AccountHealthSnapshot {
  status: "available" | "not_available" | "error";
  source: string;
  lastConfirmedAt?: string | null;
  accountStatuses: AccountHealthStatus[];
  accountHealthRatings: AccountHealthStatus[];
  metrics: AccountHealthMetric[];
  roleRequired: boolean;
  message?: string;
  limitation: string;
}

export interface ContentQualityResponse {
  waitingCount?: number;
  items: ContentQualityIssue[];
  total: number;
  nextCursor?: string | null;
  summary: ContentQualitySummary;
  accountHealth?: AccountHealthSnapshot;
  repricing?: {
    status: "ok" | "unavailable";
    source: string;
    priceChangesFailClosed: boolean;
    message?: string | null;
  };
  methodology?: ContentQualityMethodology;
}

export type MediaQualityVerdict = "good" | "review" | "poor" | "unknown" | string;

export interface MediaQualityIssue {
  code?: string;
  severity?: QualitySeverity | "warning" | "error" | string;
  message: string;
  evidenceKind?: QualityEvidenceKind;
}

export interface MediaQualityMetric {
  key?: string;
  label?: string;
  value?: string | number | boolean | null;
  unit?: string;
  status?: "pass" | "warning" | "fail" | "unknown" | string;
}

export interface MediaAsset {
  id: string;
  assetId?: string;
  sellerSku?: string;
  filename?: string;
  url?: string;
  previewUrl?: string;
  state: MediaState;
  marketplaceStates?: Partial<Record<MarketplaceCode, MediaState>>;
  role?: string;
  order?: number;
  width?: number;
  height?: number;
  rejectionReason?: string;
  qualityScore?: number;
  qualityVerdict?: MediaQualityVerdict;
  qualityIssues?: Array<MediaQualityIssue | string>;
  qualityMetrics?: MediaQualityMetric[] | Record<string, unknown>;
  qualityCheckedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface BrandLabRequirement {
  key?: string;
  label: string;
  met?: boolean;
  complete?: boolean;
  detail?: string;
}

export interface BrandLabCapabilities {
  enabled?: boolean;
  brandApproved?: boolean;
  brandRegistered?: boolean;
  brandRegistryApproved?: boolean;
  experimentsEnabled?: boolean;
  experimentsEligible?: boolean;
  amazonExperimentApiAvailable?: boolean;
  mode?: string;
  message?: string;
  reason?: string | null;
  blockers?: string[];
  requirements?: Array<BrandLabRequirement | string>;
  prerequisites?: Array<BrandLabRequirement | string>;
  features?: Array<{
    key: string;
    label: string;
    available: boolean;
  }>;
  checkedAt?: string;
  [key: string]: unknown;
}

export interface ShippingTemplate {
  id?: string;
  templateId?: string;
  name: string;
  marketplace: MarketplaceCode;
  active?: boolean;
}

export type TemplateKind = "listing" | "safety" | "compliance" | "manufacturer" | "responsible_person";

export interface SettingsTemplate {
  id: string;
  name: string;
  kind: TemplateKind | string;
  marketplace?: MarketplaceCode | null;
  values: Record<string, unknown>;
  updatedAt?: string;
}

export interface AppSettings {
  priceGuard?: {
    minimumPercent?: number;
    minimumAbsoluteEur?: number;
    ending?: number;
  };
  marketplaceBuffers?: Partial<
    Record<MarketplaceCode, { shippingEur?: number; feePercent?: number }>
  >;
  compliance?: Partial<
    Record<
      MarketplaceCode,
      {
        vatReady?: boolean;
        eprReady?: boolean;
        gpsrReady?: boolean;
        enabledForWrites?: boolean;
        note?: string;
      }
    >
  >;
  syncHours?: number[];
  timezone?: string;
}

export type JobStatus = "queued" | "running" | "retrying" | "succeeded" | "failed" | "cancelled";

export interface Job {
  id: string;
  jobId?: string;
  type: string;
  status: JobStatus;
  progress?: number;
  marketplace?: MarketplaceCode;
  sellerSku?: string;
  message?: string;
  createdAt?: string;
  startedAt?: string;
  finishedAt?: string;
  amazonRequestId?: string;
  result?: { registryClaims?: Record<string, { state?: string; reconciliationRequired?: boolean }> };
}

export interface AuditEvent {
  id: string;
  action: string;
  actor?: string;
  sellerSku?: string;
  marketplace?: MarketplaceCode;
  outcome?: string;
  summary?: string;
  requestId?: string;
  createdAt: string;
}

export interface PagedResponse<T> {
  items: T[];
  total?: number;
}

export type FinanceEventType =
  | "shipment"
  | "shipment_settle"
  | "refund"
  | "chargeback"
  | "guarantee_claim"
  | "service_fee"
  | "product_ads"
  | "adjustment"
  | "coupon"
  | "deal"
  | "removal"
  | "liquidation"
  | "retrocharge"
  | "safet_reimbursement"
  | "debt_recovery"
  | "provider_credit"
  | "unknown";

export interface FinanceKpis {
  sales: number;
  refunds: number;
  fees: number;
  ads: number;
  other: number;
  net: number;
}

export interface FinanceSyncStatus {
  lastSyncAt?: string | null;
  lastWindowFrom?: string | null;
  lastPostedBefore?: string | null;
  lastEventsSeen?: number | null;
  lastEventsNew?: number | null;
  totalEvents?: number;
}

export interface FinanceSeriesPoint {
  date: string;
  sales: number;
  refunds: number;
  costs: number;
  net: number;
}

export interface FinanceOverview {
  period: { days: number; from: string; to: string; marketplace?: string | null };
  kpis: FinanceKpis & {
    previous: FinanceKpis;
    delta: Partial<Record<keyof FinanceKpis, number | null>>;
  };
  orders: number;
  unitsSold: number;
  unitsRefunded: number;
  series: FinanceSeriesPoint[];
  feeTypes: Array<{ type: string; amount: number; count: number }>;
  marketplaces: Array<{ code: string; sales: number; fees: number; net: number }>;
  eventTypes: Array<{ type: FinanceEventType | string; label: string; count: number; total: number }>;
  sync: FinanceSyncStatus;
}

export interface FinanceBreakdownPart {
  kind: "charge" | "fee" | "promotion" | "other" | string;
  type: string;
  amount: number;
}

export interface FinanceEventItem {
  id: string;
  postedAt?: string | null;
  postedAtEstimated?: boolean;
  type: FinanceEventType | string;
  typeLabel: string;
  amazonOrderId?: string | null;
  sellerSku?: string | null;
  marketplace?: MarketplaceCode | string | null;
  marketplaceName?: string | null;
  currency: string;
  quantity?: number | null;
  revenue: number;
  fees: number;
  promotions: number;
  other: number;
  total: number;
  breakdown: FinanceBreakdownPart[];
  description?: string;
}

export interface FinanceEventsResponse {
  items: FinanceEventItem[];
  total: number;
}

export interface SkuProfitItem {
  sellerSku: string;
  title: string;
  asin?: string | null;
  imageUrl?: string | null;
  fulfillmentMode?: FulfillmentMode | null;
  currentPrice?: number | null;
  marketplaces: string[];
  unitsSold: number;
  unitsRefunded: number;
  sales: number;
  refunds: number;
  fees: number;
  other: number;
  netProceeds: number;
  unitCost?: number | null;
  cogs?: number | null;
  profit?: number | null;
  marginPercent?: number | null;
  refundRatePercent?: number | null;
  flags: Array<"missing_cost" | "negative_profit" | "high_refund_rate" | string>;
}

export interface SkuProfitResponse {
  items: SkuProfitItem[];
  summary: {
    skuCount: number;
    unitsSold: number;
    sales: number;
    fees: number;
    refunds: number;
    netProceeds: number;
    profitKnown: number;
    missingCostCount: number;
    negativeProfitCount: number;
  };
}

export interface SkuCostEntry {
  sellerSku: string;
  purchaseCostNet?: number | null;
  packagingCost: number;
  shippingCost: number;
  otherCost: number;
  currency: string;
  note: string;
  unitCost?: number | null;
  updatedAt?: string | null;
}

export interface FeeEstimateEntry {
  sellerSku: string;
  marketplace: MarketplaceCode | string;
  price: number;
  currency: string;
  totalFees?: number | null;
  feeBreakdown: Array<{ type: string; amount: number }>;
  status: "ok" | "error" | string;
  message?: string | null;
  estimatedAt?: string | null;
}

export interface ProfitPreviewResult {
  sellerSku: string;
  marketplace: MarketplaceCode | string;
  price: number;
  currency: string;
  vatRatePercent: number;
  netRevenue: number;
  feesEstimated?: number | null;
  feeSource?: {
    status: "ok" | "missing" | "error" | string;
    basePrice?: number;
    estimatedAt?: string | null;
    scaled?: boolean;
    message?: string | null;
  };
  feeEstimate?: FeeEstimateEntry | null;
  unitCost?: number | null;
  cost?: SkuCostEntry | null;
  profit?: number | null;
  marginPercent?: number | null;
  breakEvenPrice?: number | null;
  targetMarginPercent?: number | null;
  targetMarginPrice?: number | null;
  listingStatus?: ListingStatus | string;
  assumptions: string[];
}

export interface FinanceAlert {
  severity: "critical" | "warning" | "info";
  kind: string;
  title: string;
  message: string;
  sellerSku?: string;
  marketplace?: string;
  value?: number;
}

export interface FinanceAlertsResponse {
  items: FinanceAlert[];
  counts: { critical: number; warning: number; info: number };
  periodDays: number;
}

export interface FinanceDashboardSummary extends FinanceKpis {
  days: number;
  lastSyncAt?: string | null;
}



export interface RolloutPreparation {
  productType: string;
  asin: string;
  marketplaces: Array<{
    code: MarketplaceCode;
    catalogAvailable?: boolean;
    attributes?: Record<string, unknown>;
    definition?: ProductTypeDefinitionResponse;
    sourceTitle?: string;
    message?: string;
    error?: string;
  }>;
}

export type ProductReviewKind = "images" | "safety";
export interface CatalogImageCase {
  id: string;
  revision: number;
  sellerSku: string;
  marketplaceCode: MarketplaceCode;
  asin: string;
  imageRole: string;
  imageUrl: string;
  imageUrls: string[];
  reason: string;
  caseReference?: string | null;
  status: "waiting" | "needs_check" | "check_failed" | "resolved" | "withdrawn";
  openedAt: string;
  checkAfter: string;
  nextCheckAt?: string | null;
  lastCheckedAt?: string | null;
  lastCheckError?: string | null;
  resolvedAt?: string | null;
}
export interface CatalogImageCaseOpen {
  sellerSku: string;
  targets: Array<{ marketplace: MarketplaceCode; expectedRevision: number; reviewToken: string }>;
  imageUrl: string;
  reason: string;
  caseReference?: string;
  confirmed: true;
}
export interface CatalogImageCaseOpenResult {
  items: Array<{ marketplaceCode: MarketplaceCode; status: string; case?: CatalogImageCase; revision?: number; error?: string }>;
}
export interface ProductReviewRow {
  sellerSku: string;
  germanTitle?: string | null;
  marketplaceCode: MarketplaceCode;
  asin?: string | null;
  title: string;
  quantity: number | null;
  stockKnown?: boolean;
  stockSource?: string;
  imageUrl?: string | null;
  imagesDue: boolean;
  safetyDue: boolean;
  imagesReviewedAt?: string | null;
  imagesNextDueAt?: string | null;
  safetyReviewedAt?: string | null;
  imageStatus: string;
  safetyStatus: string;
  marketplaces?: ProductReviewRow[];
  catalogCases?: CatalogImageCase[];
}
export interface ProductReviewsResponse {
  items: ProductReviewRow[];
  summary: ProductReviewSummary;
  total: number;
  page: number;
  pageSize: number;
}
export interface ProductReviewThumbnail {
  sellerSku: string;
  marketplaceCode: MarketplaceCode;
  asin: string;
  imageUrls: string[];
}
export interface ReviewImageRendition {
  url: string;
  width?: number;
  height?: number;
}
export interface ReviewImage {
  role: string;
  url?: string;
  assetId?: string;
  attributeName?: string;
  width?: number;
  height?: number;
  renditions?: ReviewImageRendition[];
  readOnly?: boolean;
}
export interface SafetyReviewField {
  name: string;
  label: string;
  description?: string;
  currentValue: unknown;
  schema: Record<string, unknown>;
  editable: boolean;
  required: boolean;
  reason?: string;
}
export interface ProductReviewJobInfo {
  id: string;
  status: string;
  message?: string | null;
  createdAt?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  parent?: ProductReviewJobInfo;
}
export interface AmazonListingIssue {
  code?: string | number;
  message?: string;
  severity?: string;
  attributeNames?: string[];
  categories?: string[];
  enforcements?: unknown;
}
export interface ProductReviewDifference {
  attribute: string | null;
  expected?: unknown;
  actual?: unknown;
  issue?: AmazonListingIssue;
}
export interface ProductReviewWorkflow {
  jobId?: string | null;
  queuedAt?: string | null;
  submittedAt?: string | null;
  submissionAttemptedAt?: string | null;
  job?: ProductReviewJobInfo | null;
  lastCheckedAt?: string | null;
  lastCheckError?: string | null;
  nextCheckAt?: string | null;
  verificationExhausted?: boolean;
  unresolvedAcceptance?: boolean;
  verificationDifferences?: ProductReviewDifference[];
  processingErrors?: Array<AmazonListingIssue & { attribute?: string | null }>;
  amazonIssues?: AmazonListingIssue[];
  submissionIssues?: AmazonListingIssue[];
}
export interface CatalogImageLoadDiagnostic {
  source: "standard" | "images-only";
  outcome: "ready" | "failed";
  message: string;
  httpStatus?: number;
  requestId?: string;
  amazonCodes?: string[];
}
export interface ImageReadbackPair {
  attribute: string;
  role: string;
  submittedUrl: string;
  amazonUrl: string;
}
export interface ImageReadbackComparison {
  jobId: string;
  reviewToken: string;
  pairs: ImageReadbackPair[];
}
export interface ProductReviewDetail {
  imageProblems?: ProductReviewProblemCountry[];
  stockKnown?: boolean;
  sellerSku: string;
  germanTitle?: string | null;
  marketplaceCode: MarketplaceCode;
  asin?: string | null;
  title: string;
  quantity: number | null;
  revision: number;
  productType?: string | null;
  amazonIssues?: AmazonListingIssue[];
  images: ProductReviewWorkflow & {
    readbackComparison?: ImageReadbackComparison | null;
    catalogDiagnostics?: CatalogImageLoadDiagnostic[];
    catalogCases?: CatalogImageCase[];
    due?: boolean;
    nextDueAt?: string | null;
    current: ReviewImage[];
    catalog: ReviewImage[];
    draft: ReviewImage[];
    draftNeedsRefresh?: boolean;
    supportedRoles: string[];
    mutationToken?: string | null;
    reviewToken: string | null;
    loadStatus?: "ready" | "partial" | "empty" | "unavailable";
    loadMessage?: string | null;
    reviewBlockedReason?: string | null;
    status: string;
    message?: string | null;
    submissionId?: string | null;
    reviewedAt?: string | null;
    verifiedAt?: string | null;
  };
  safety: ProductReviewWorkflow & {
    due?: boolean;
    fields: SafetyReviewField[];
    warnings: string[];
    supported: boolean;
    reviewToken: string;
    status: string;
    message?: string | null;
    submissionId?: string | null;
    reviewedAt?: string | null;
    verifiedAt?: string | null;
  };
  warnings: string[];
}
export interface ProductReviewPreview {
  previewId: string;
  confirmationToken: string;
  expiresAt: string;
  canSubmit: boolean;
  patches: Array<{ op: string; path: string; value?: unknown }>;
  diff: Array<{ attribute: string; before?: unknown; after?: unknown }>;
  warnings: string[];
}
export interface ProductReviewIdentity {
  sellerSku: string;
  marketplace: MarketplaceCode;
  expectedRevision: number;
}
export interface ProductReviewSubmit {
  previewId: string;
  confirmationToken: string;
  idempotencyKey: string;
}
export interface ProductReviewVerification {
  status: string;
  verifiedAt?: string | null;
  differences: ProductReviewDifference[];
}

export interface ProductReviewBatchDetail {
  imageProblems?: ProductReviewProblemCountry[];
  sellerSku: string;
  germanTitle?: string | null;
  items: Array<{ marketplaceCode: MarketplaceCode; detail?: ProductReviewDetail; error?: string }>;
}
export interface ProductReviewBatchIdentity {
  sellerSku: string;
  sourceMarketplace: MarketplaceCode;
  sourceExpectedRevision: number;
  targets: Array<{ marketplace: MarketplaceCode; expectedRevision: number }>;
}
export interface ProductReviewBatchPreview {
  previewId: string;
  confirmationToken: string;
  expiresAt: string;
  canSubmit: boolean;
  kind: ProductReviewKind;
  items: Array<{
    marketplaceCode: MarketplaceCode;
    status: "ready" | "unchanged" | "error";
    patches: ProductReviewPreview["patches"];
    diff: ProductReviewPreview["diff"];
    warnings: string[];
    error?: string;
  }>;
  warnings: string[];
}
export interface ProductReviewBatchResultItem {
  marketplaceCode: MarketplaceCode;
  status: string;
  error?: string;
  message?: string | null;
  submissionId?: string | null;
  verifiedAt?: string | null;
  reviewedAt?: string | null;
  revision?: number;
  differences?: ProductReviewVerification["differences"];
}
export interface ProductReviewBatchResult {
  items: ProductReviewBatchResultItem[];
}


export interface ProductReviewSummary {
  imagesDue: number;
  safetyDue: number;
  problemsDue?: number;
}
export interface ProductReviewProblemReason {
  code: string;
  source: string;
  message: string;
  httpStatus?: number;
  requestId?: string;
  amazonCodes?: string[];
}
export type ProblemResolutionKind = "missing_title" | "gtin_brand" | "toy_documents" | "trademark" | "product_safety" | "catalog_removed" | "catalog_lookup" | "other";
export interface ProblemResolutionPlan {
  id: string;
  kind: ProblemResolutionKind;
  title: string;
  summary: string;
  steps: string[];
  evidenceChecklist: string[];
  issueId?: string;
  evidenceKey: string;
  originalMessages: Array<{ code?: string; message: string; source?: string; observedAt?: string }>;
  sellerCentralTarget: "inventory" | "performance" | "help";
  supportFields: { request: string; steps: string; references: string };
  apiAction?: { type: "missing_title"; issueId: string };
}
export interface ProblemTitleContext {
  issueId: string;
  evidenceKey: string;
  sellerSku: string;
  marketplaceCode: MarketplaceCode;
  asin: string;
  productType: string;
  languageTag: string;
  currentValue: unknown;
  currentTitle: string | null;
  suggestedTitle: string | null;
  editable: boolean;
  reason?: string;
  warnings: string[];
  checkedAt: string;
  constraints?: { minLength?: number; maxLength?: number };
}
export interface ProblemTitlePreview {
  previewId: string;
  confirmationToken: string;
  expiresAt: string;
  canSubmit: boolean;
  issueId: string;
  sellerSku: string;
  marketplaceCode: MarketplaceCode;
  asin: string;
  languageTag: string;
  before: unknown;
  after: unknown;
  diff: Array<{ path: string; field: string; operation: string; before: unknown; after: unknown }>;
  warnings: string[];
}
export interface ProblemTitleCheck {
  jobId: string;
  status: "accepted" | "verified" | "pending" | "rejected" | "unknown";
  verified: boolean;
  message: string;
  submissionId?: string;
  requestId?: string;
  checkedAt?: string;
}
export interface ProductReviewProblemCountry {
  sellerSku: string;
  marketplaceCode: MarketplaceCode;
  asin?: string | null;
  title: string;
  germanTitle?: string | null;
  quantity: number | null;
  stockKnown: boolean;
  offerStatus?: string | null;
  imagesDue?: boolean;
  imagesReviewedAt?: string | null;
  activeBlocker: boolean;
  reasons?: ProductReviewProblemReason[];
  resolutionLabels?: string[];
  resolutionPlans?: ProblemResolutionPlan[];
  firstSeenAt?: string | null;
  lastCheckedAt?: string | null;
  lastCheckError?: { message?: string; httpStatus?: number; code?: string; requestId?: string; amazonCodes?: string[] } | null;
  catalogDiagnostics?: CatalogImageLoadDiagnostic[];
}
export interface ProductReviewProblemRow {
  sellerSku: string;
  title: string;
  germanTitle?: string | null;
  asin?: string | null;
  countries: ProductReviewProblemCountry[];
  problemCountries: ProductReviewProblemCountry[];
  activeBlocker: boolean;
}
export interface ProductReviewProblemsResponse {
  items: ProductReviewProblemRow[];
  summary: ProductReviewSummary;
  total: number;
  page: number;
  pageSize: number;
}
export interface ProductReviewProblemDetail extends ProductReviewProblemRow {
  status: "blocked" | "ready" | "not_eligible" | "unchecked";
  imageReviewDisposition?: "due" | "archived" | "not_eligible" | "unchecked";
  checkError?: string;
}

export interface CatalogImagePreparation {
  imageUrls: string[];
  id: string;
  sellerSku: string;
  marketplaceCode: MarketplaceCode;
  asin: string;
  imageUrl: string;
  reason: string;
  status: "prepared" | "opened" | "cancelled" | "continued";
  createdAt: string;
  submissionJobId: string | null;
  submissionId: string | null;
  submissionStatus: "not_submitted" | "pending" | "verified" | "failed" | "superseded";
  caseId?: string | null;
}

export interface CorrectionWorkflow {
  id: string;
  sellerSku: string;
  marketplaceCode: MarketplaceCode;
  asin: string;
  wrongImages: Array<{ role: string; url: string; urls: string[] }>;
  correctImages?: Array<{ role: string; url: string; urls: string[]; source?: "catalog" | "seller_contribution" }>;
  visibleWrongImages?: Array<{ role: string; url: string; urls: string[] }>;
  reason: string;
  mode: "online" | "upload";
  status: "awaiting_upload" | "upload_failed" | "processing" | "waiting" | "review_due" | "ready_for_case" | "ready_to_complete";
  createdAt: string;
  waitUntil?: string | null;
  submissionId?: string | null;
  caseIds: string[];
  caseReference?: string | null;
  revision: number;
  allWrongImagesGone?: boolean;
}
